import {
	createRoute,
	isRedirect,
	redirect,
	type AnyRootRoute,
} from '@tanstack/react-router';

import { issueOptions, issuesOptions, previewIssueOptions } from '@shared/api';
import { byReleaseDate } from '@shared/lib/hooks';
import type { Issue } from '@/constants';
// Straight from the model file, not the `@pages/issue` barrel -- the barrel
// also re-exports the heavy `Issue` UI component (`./ui/issue`), and this
// file is eagerly imported by every template's router. Going through the
// barrel would make `Issue` statically reachable everywhere, defeating any
// template's `lazy(() => import('@pages/issue'))` for it (see
// improvements/10-performance-bundle-size.md).
import {
	issueSearchSchema,
	type IssueSearch,
} from '@pages/issue/model/issue.hooks';
// Same deep-import reasoning as above: `pages/issue/ui/loading` specifically
// (not the `ui/index.ts` sub-barrel, which also re-exports `IssueReaderShell`
// -- itself lightweight, but there's no reason to pull in more than this
// route needs). `Loading` only depends on `@shared/ui/topbar`, cheap enough
// to import eagerly so it can stand in for the generic `RoutePending`
// fallback below while the route's own lazy chunk is still in flight --
// see `issueRoute`'s `pendingComponent`.
import { Loading } from '@pages/issue/ui/loading';
// Same reasoning, for the two `preview` catalog routes -- see each route's
// own `pendingComponent` below.
import { PreviewRouteLoading as PreviewBasicRouteLoading } from '@pages/preview-basic/ui/route-loading';
import { PreviewRouteLoading as PreviewViewRouteLoading } from '@pages/preview/ui/route-loading';
import { FavoriteRouteLoading } from '@pages/favorite/ui/route-loading';
import type { RouteProtection } from './route-protection';
import type { RouterContext } from './router-context';
import { tokenAuthGuard } from './token-auth-guard';

/**
 * Canonical, reusable routes shared across templates -- the "route catalog"
 * named in improvements/07-template-architecture.md's actionable plan step 3.
 * Each entry here points at a component genuinely shared by two or more
 * templates today (confirmed by reading every `App.router.tsx`, not
 * assumed) -- everything template-exclusive stays out of this file and is
 * passed to `createTenantApp`'s `custom` escape hatch instead.
 *
 * `home` is deliberately NOT here: every template's home page is bespoke UI
 * (each has its own home/Home.tsx), so it stays a manifest-supplied slot in
 * createTenantApp instead.
 *
 * **Protection is not declared here.** A canonical route's identity (path,
 * parent, search schema) is the same in every template that has it; whether
 * it's protected is not -- and it isn't fixed per template either, since one
 * template serves tenants with different `config.app.auth` settings. So the
 * resolved guard is passed in per build, and this file stays purely about
 * identity -- see route-protection.ts for the full reasoning.
 *
 * **Code splitting** (topic 08, step 7): every route below is split with the
 * router's own `route.lazy()` rather than `React.lazy()`. See
 * `lazy/README.md` for why the dynamic-import boundary lives in `app/routing/
 * lazy/` instead of inside each page slice, and what `route.lazy()` buys over
 * `React.lazy()` (short version: `defaultPreload: 'intent'` can warm a
 * `route.lazy()` chunk on hover; it cannot see a `React.lazy()` one).
 */

/**
 * Declared explicitly rather than derived as `keyof RouteCatalog`: the catalog
 * builder now *takes* a protection map keyed by these names, so deriving them
 * from its return type would make the type reference itself. `assertCatalogKeys`
 * at the bottom of this file keeps the two in sync at compile time.
 */
export type RouteCatalogKey =
	| 'issue'
	| 'issueArticle'
	| 'issuesLatest'
	| 'drafts'
	| 'favorite'
	| 'callback'
	| 'logout'
	| 'subscriptions'
	| 'subscriptionsSuccess'
	| 'preview'
	| 'previewView';

/**
 * Already resolved against the tenant's auth config by `createTenantApp` --
 * this file never sees `'auth'` or a template's wiring, only the concrete
 * guard that came out of `resolveProtection`.
 */
export type RouteProtectionMap = Partial<
	Record<RouteCatalogKey, RouteProtection>
>;

/**
 * Turns a template's `RouteProtection` into a `beforeLoad`. Returning
 * `provide` from `beforeLoad` is how TanStack merges values into the route
 * context that the route's lazily-loaded component (and its children) read.
 */
function toBeforeLoad(protection?: RouteProtection) {
	if (!protection) return undefined;
	return async (opts: {
		params: Record<string, string>;
		search: Record<string, unknown>;
		location: { pathname: string };
	}) => {
		await protection.beforeLoad?.({
			params: opts.params,
			search: opts.search,
			pathname: opts.location.pathname,
		});
		return protection.provide;
	};
}

/**
 * `issue`/`issueArticle`'s fallback when a template hasn't declared its own
 * `protect.issue`/`protect.issueArticle` -- the `beforeLoad` equivalent of
 * `lazy/issue.lazy.tsx`'s old default render-time gate. `tokenAuthGuard`
 * no-ops entirely unless the tenant is actually on `token` auth, so this is
 * safe to fall back to unconditionally: a template with its own Cognito/
 * reader-family guard always has `protect.issue` set (see
 * `route-protection.ts`'s `resolveProtection`), so it never reaches this
 * default in the first place. In practice this is what `sections`, `poets`,
 * and `morningstar` get today.
 */
const DEFAULT_ISSUE_PROTECTION: RouteProtection = {
	beforeLoad: (context) =>
		tokenAuthGuard(context, { redirectIfUnauthenticated: true }),
};

interface LoaderArgs {
	context: RouterContext;
	params: { id: string };
	deps: Pick<IssueSearch, 'limit' | 'sortKey' | 'reverse'>;
}

interface PreviewLoaderArgs {
	context: RouterContext;
	params: { id: string };
}

export function buildRouteCatalog(
	rootRoute: AnyRootRoute,
	protect: RouteProtectionMap = {},
) {
	// Topic 08's loader item: `issueOptions` is the same factory `useIssue`
	// calls, so this starts the fetch as soon as `issues/$id` matches instead
	// of waiting for the lazy component chunk to load and mount before the
	// query even starts. `issueArticleRoute` (below) doesn't need its own
	// loader -- it's nested under this route, so its `queryClient` is the
	// same instance this loader already populated by the time the child
	// resolves; `useLoad()` picks the current article out of the same
	// `issue.articles` this ensures. `loaderDeps` re-runs the loader when
	// `limit`/`sortKey`/`reverse` change, matching `useIssue`'s queryKey,
	// which includes them for the same reason (different sort/limit views of
	// the same issue shouldn't collide in the cache).
	const issueRoute = createRoute({
		getParentRoute: () => rootRoute,
		validateSearch: issueSearchSchema,
		beforeLoad: toBeforeLoad(protect.issue ?? DEFAULT_ISSUE_PROTECTION),
		path: 'issues/$id',
		// Overrides `createTenantApp`'s generic `defaultPendingComponent`
		// (`RoutePending` -- a bare gray-box skeleton with no real navbar)
		// for this route specifically. `Loading` is the same real-navbar-
		// plus-skeleton-content component `IssueReaderShell` shows while the
		// issue query itself resolves (improvements/
		// 07.1-per-template-issue-reader-widgets.md) -- reusing it here means
		// the navbar is real and in place from the very first frame of
		// navigating to an issue, not just once the page's own chunk has
		// already loaded and mounted.
		pendingComponent: Loading,
		loaderDeps: ({ search }) => ({
			limit: search.limit,
			sortKey: search.sortKey,
			reverse: search.reverse,
		}),
		loader: ({ context, params, deps }: LoaderArgs) => {
			// Warmed in parallel with the issue query, not awaited: `Issue`
			// (pages/issue/ui/issue.tsx) wraps these same three modules in
			// its own `lazy()` calls, and a dynamic import() of the same
			// resolved specifier reuses whatever promise is already
			// in-flight/settled rather than starting a fresh one. Without
			// this, `<LazyAudioPlayer>{...<LazyResponsive/Replica>}</...>`'s
			// nesting meant React discovered the second chunk only *after*
			// the first one resolved and rendered -- two sequential
			// suspends against the single root Suspense boundary
			// (`root-route.tsx`), each one flashing the *entire* outlet
			// back to `RoutePending`. Most visible in dev, where every
			// chunk is its own uncached network round trip; prod's shared
			// bundling/HTTP caching mostly hides it, but the double-suspend
			// is real there too on a cold cache.
			// Best-effort: a failed warm-up isn't fatal, `Issue`'s own
			// `lazy()` calls will just fetch it themselves at render time --
			// swallow the rejection rather than let it surface as an
			// unhandled one.
			Promise.all([
				import('@widgets/player'),
				import('@widgets/issue-reader/responsive/responsive'),
				import('@widgets/issue-reader/replica/replica'),
			]).catch(() => {});

			// Swallowed, not returned, for the same reason
			// `previewViewRoute`'s own loader below swallows its rejection:
			// `fetchIssue` (shared/api/queries.ts) now *throws* when offline
			// with nothing cached, rather than resolving a fake `{ issue:
			// null }` that the persister would then write over the reader's
			// real cached copy. That rejection has to reach here as a real
			// rejection for the throw to be worth anything -- but a
			// route-level crash ("You're offline. This page has not been
			// downloaded for offline reading.") is not the right response to
			// it either, since the component re-derives the same signal
			// itself once it mounts (`useIssueView`'s own
			// `isUnavailableOffline` branch). Returning the promise made
			// every offline miss a full-page dead end whose "Try again"
			// re-ran this same loader into the same rejection.
			return context.queryClient
				.ensureQueryData(
					issueOptions(params.id, {
						isAdmin: context.auth.isAdmin(),
						ttlMinutes: context.config.app?.cache?.ttl?.getIssue,
						...deps,
						// Same reasoning as `pages/article/article.route.tsx`'s
						// `onUnavailableOffline`: `useNavigate()` isn't available
						// in a loader, so a session that can't be refreshed sends
						// the reader home via `redirect()` instead of `useIssue`'s
						// own `navigate({ to: '/' })` (used when this factory is
						// called from the component instead).
						onSessionExpired: () => {
							throw redirect({
								to: context.config.build?.subdirectory || '/',
								replace: true,
							});
						},
					}),
				)
				.catch((error: unknown) => {
					// Everything except a router `redirect()` is swallowed (see
					// the comment above). `onSessionExpired` throws one of those
					// deliberately, and a redirect is control flow, not a
					// failure -- swallowing it would strand an expired session
					// on a route it can no longer load instead of bouncing it
					// home. `isRedirect` is TanStack's own check for exactly
					// this, rather than duck-typing the thrown value here.
					if (isRedirect(error)) {
						throw error;
					}
				});
		},
	}).lazy(() =>
		import('./lazy/issue.lazy').then((m) => m.IssueRouteComponent),
	);

	const issueArticleRoute = createRoute({
		getParentRoute: () => issueRoute,
		validateSearch: issueSearchSchema,
		beforeLoad: toBeforeLoad(protect.issueArticle ?? DEFAULT_ISSUE_PROTECTION),
		path: '$articleId',
		// Same reasoning as `issueRoute` above.
		pendingComponent: Loading,
	}).lazy(() =>
		import('./lazy/issue.lazy').then((m) => m.IssueArticleRouteComponent),
	);

	// The target for a tenant's manifest `shortcuts` entry (see
	// docs/wiki/Manifest-Shortcuts.md and config.type.ts's `Shortcut`) --
	// "latest issue" has no static route of its own to point a shortcut's
	// build-time `url` at (issues only exist as `issues/$id`), so this
	// resolves it at request time instead: fetch the issues list, pick the
	// newest, redirect into it. Never renders anything of its own -- the
	// loader always throws a `redirect()` before there's a component to
	// mount, so there's no `.lazy()` here.
	//
	// Guarded the same as `issue`/`issueArticle` (not left unprotected)
	// specifically so an unauthenticated reader is bounced to login *before*
	// this fetches anything, rather than fetching, redirecting to
	// `issues/$id`, and only then hitting that route's own guard.
	const issuesLatestRoute = createRoute({
		getParentRoute: () => rootRoute,
		beforeLoad: toBeforeLoad(protect.issuesLatest ?? DEFAULT_ISSUE_PROTECTION),
		path: 'issues/latest',
		loader: async ({ context }: { context: RouterContext }) => {
			// Same factory, same query key, no cache-option guessing as
			// `home-loaders.ts`'s `homeIssuesLoader` -- see that file's own
			// doc comment for why a loader shouldn't guess `isOnline`. A
			// template whose home already prefetched this gets a cache hit
			// here, not a second fetch.
			const data = await context.queryClient
				.ensureQueryData(issuesOptions({}))
				.catch(() => undefined);
			// Same null-filtering and sort `useIssues()` (shared/lib/hooks/
			// use-issue.ts) applies to the identical query -- kept in sync
			// with that function rather than trusting the server's own
			// ordering, for the same reason it doesn't either.
			const issues = (data?.issues ?? []).filter(
				(issue): issue is NonNullable<typeof issue> => issue != null,
			) as unknown as Array<Issue>;
			const latest = [...issues].sort(byReleaseDate)[0];

			throw redirect(
				latest
					? { to: '/issues/$id', params: { id: latest.id }, replace: true }
					: {
							to: context.config.build?.subdirectory || '/',
							replace: true,
						},
			);
		},
	});

	// Admin-gated in every template that has it (basic, daily, magazine,
	// magshop, morningstar, padi, poets, sections, starmagazine, toyota --
	// all ten) via `if (config?.app?.admin) routes.push(draftsRoute)`.
	// createTenantApp applies that same filter, not this file.
	const draftsRoute = createRoute({
		getParentRoute: () => rootRoute,
		beforeLoad: toBeforeLoad(protect.drafts),
		path: 'drafts',
	}).lazy(() => import('./lazy/drafts.lazy').then((m) => m.DraftsRoute));

	// Shared by morningstar + poets, both gated by config.app.favorite.
	const favoriteRoute = createRoute({
		getParentRoute: () => rootRoute,
		beforeLoad: toBeforeLoad(protect.favorite),
		path: 'favorite/$id',
		// Same reasoning as `issueRoute`/`previewViewRoute` above: without
		// this, the window before `Favorite` mounts fell back to the
		// generic `defaultPendingComponent` (`RoutePending`), which has no
		// real navbar.
		pendingComponent: FavoriteRouteLoading,
	}).lazy(() => import('./lazy/favorite.lazy').then((m) => m.FavoriteRoute));

	// Shared by basic, magazine, daily, starmagazine -- confirmed identical
	// shape (no validateSearch) across all four. magshop has its own,
	// differently-shaped callback (validateSearch: callbackSearchSchema) and
	// padi has its own separate fork; neither opts into this entry.
	const callbackRoute = createRoute({
		getParentRoute: () => rootRoute,
		beforeLoad: toBeforeLoad(protect.callback),
		path: 'callback',
	}).lazy(() => import('./lazy/callback.lazy').then((m) => m.CallbackRoute));

	// Shared by basic, magazine, daily, starmagazine. magshop has its own
	// Logout backed by its own useLogout/Auth.hooks -- not this entry.
	const logoutRoute = createRoute({
		getParentRoute: () => rootRoute,
		beforeLoad: toBeforeLoad(protect.logout),
		path: 'logout',
	}).lazy(() => import('./lazy/logout.lazy').then((m) => m.LogoutRoute));

	// Shared by basic, magazine, daily, starmagazine.
	const subscriptionsRoute = createRoute({
		getParentRoute: () => rootRoute,
		beforeLoad: toBeforeLoad(protect.subscriptions),
		path: 'subscriptions',
	}).lazy(() =>
		import('./lazy/subscriptions.lazy').then((m) => m.SubscriptionsRoute),
	);

	// Deliberate, reviewed drift fix (same shape as sections' `drafts` fix in
	// the previous migration pass): basic/magazine already nested `success`
	// under `subscriptionsRoute`; daily/starmagazine instead declared it as
	// a root-level sibling with the literal path `subscriptions/success`.
	// Both resolve to the same fullPath ('/subscriptions/success'), but the
	// catalog can only give a route one parent -- nesting under
	// `subscriptionsRoute` is the shape two of the four already used, so
	// daily/starmagazine move to match rather than the other way around.
	const subscriptionsSuccessRoute = createRoute({
		getParentRoute: () => subscriptionsRoute,
		beforeLoad: toBeforeLoad(protect.subscriptionsSuccess),
		path: 'success',
	}).lazy(() =>
		import('./lazy/subscriptions.lazy').then(
			(m) => m.SubscriptionsSuccessRoute,
		),
	);

	// `@pages/preview-basic/Preview` -- shared by basic + magazine.
	const previewRoute = createRoute({
		getParentRoute: () => rootRoute,
		beforeLoad: toBeforeLoad(protect.preview),
		path: 'preview/$id',
		pendingComponent: PreviewBasicRouteLoading,
	}).lazy(() =>
		import('./lazy/preview-basic.lazy').then((m) => m.PreviewRoute),
	);

	// `@pages/preview/Preview` -- a *different* component, shared by
	// morningstar + poets. Not the same implementation as `preview` above;
	// kept as a separate entry rather than forced together.
	const previewViewRoute = createRoute({
		getParentRoute: () => rootRoute,
		beforeLoad: toBeforeLoad(protect.previewView),
		path: 'preview/$id',
		pendingComponent: PreviewViewRouteLoading,
		// This route's chunk/loader routinely resolves in well under a
		// second (often under 300ms on a warm CDN cache) -- the library
		// default (`pendingMs: 1000`) already covers most of that, but
		// setting it explicitly here matches the 300ms threshold this
		// route's own `pendingComponent` (`PreviewViewRouteLoading` ->
		// `PreviewSkeleton`) was designed around, instead of leaving the
		// two implicitly-related numbers to drift apart. `pendingMinMs`
		// matched to the same value for the same reason `pendingMs` exists
		// in the first place: once the skeleton *does* show, it shouldn't
		// vanish again faster than a reader can register it (user-reported
		// 2026-08-27 -- a spinner/skeleton flash felt more jarring than no
		// loading UI at all for a load this fast).
		pendingMs: 300,
		pendingMinMs: 300,
		// Same reasoning as `issueRoute`'s own loader above: without this,
		// `defaultPreload: 'intent'` (create-tenant-app.tsx) only ever
		// preloaded this route's lazy *chunk* on hover -- the actual issue
		// data fetch (`useGetIssue`, entities/issue) only started once
		// `Content()` mounted, i.e. after the click, not the hover.
		// `previewIssueOptions` is the exact factory `useGetIssue` itself
		// calls (`entities/issue/api/use-get-issue.ts`), same
		// `queryKeys.preview(id)` key either populates, so a genuine
		// hover-then-click reuses this already-in-flight/settled request
		// instead of starting a second one. Confirmed safe for this route's
		// cover-morph `viewTransition` (user asked before this was added):
		// TanStack Router fully awaits a route's loader and only calls
		// `document.startViewTransition()` once that data has resolved --
		// the transition's "after" snapshot is always the real, data-loaded
		// page, never a mid-load skeleton frame.
		loader: async ({ context, params }: PreviewLoaderArgs) => {
			try {
				await context.queryClient.ensureQueryData(
					previewIssueOptions(params.id, {
						isAdmin: context.auth.isAdmin(),
						ttlMinutes: context.config.app?.cache?.ttl?.getIssue,
					}),
				);
			} catch {
				// Offline with nothing cached for this issue --
				// `fetchPreviewIssue` (shared/api/queries.ts) throws rather
				// than resolving to a fake `{ issue: null }` specifically so
				// this rejection doesn't get silently swallowed *into the
				// cache* -- it needs to reach here as a real rejection so
				// this catch can swallow it instead of TanStack Router's
				// `RouteError`. A route-level crash isn't the right UI for
				// "you're offline and haven't seen this issue yet";
				// `Content()` (pages/preview/ui/preview.tsx) already renders
				// `OfflineContent` for exactly that once it mounts and
				// re-derives the same "nothing cached" signal itself.
			}
		},
	}).lazy(() =>
		import('./lazy/preview-view.lazy').then((m) => m.PreviewViewRoute),
	);

	return {
		issue: issueRoute,
		issueArticle: issueArticleRoute,
		issuesLatest: issuesLatestRoute,
		drafts: draftsRoute,
		favorite: favoriteRoute,
		callback: callbackRoute,
		logout: logoutRoute,
		subscriptions: subscriptionsRoute,
		subscriptionsSuccess: subscriptionsSuccessRoute,
		preview: previewRoute,
		previewView: previewViewRoute,
	};
}

export type RouteCatalog = ReturnType<typeof buildRouteCatalog>;

/**
 * Fails the build if `RouteCatalogKey` and the catalog's actual keys drift
 * apart -- e.g. a new catalog entry added without a name to protect it by.
 */
type AssertCatalogKeys =
	[RouteCatalogKey] extends [keyof RouteCatalog]
		? [keyof RouteCatalog] extends [RouteCatalogKey]
			? true
			: never
		: never;
const assertCatalogKeys: AssertCatalogKeys = true;
void assertCatalogKeys;
